'use client'

import { useState, useEffect, useId, useRef, useCallback } from 'react'
import Tooltip from '@/components/ui/Tooltip'
import AppIcon from '@/components/content/app/AppIcon'
import { Icon } from '@/icons/Icon'
import { cn, timeToSeconds, formatDuration, getBrandColors, isPopulatedObject } from '@/lib/utils'
import { checkAppBranding } from '@/lib/formatting'
import { getVideoSource, getThumbnail, normalizeThumbnailRef } from './helpers'
import VideoThumbnailImage from './VideoThumbnailImage'
import YoutubePreconnect from './YoutubePreconnect'
import { useSegmentPlayer } from '@/components/content/video-segment/SegmentPlayerContext'
import type { Video, VideoAppsApp, App } from '@/payload-types'

type VideoProps = {
  id?: string
  video: Pick<
    Video,
    'id' | 'thumbnail' | 'name' | 'url' | 'duration' | 'youtubeId' | 'thumbnailOverride'
  >
  videoApps?: VideoAppsApp[]
  timestamp?: string
  externalHighlightedApp?: string | null
  isExpanded?: boolean
  setIsPlayingAction?: (value: boolean) => void
  onMarkerClickAction?: (timestamp: string) => void
  timestampEnd?: string | null
  withTitle?: boolean
  index?: number
  onClickAction?: () => void
  defaultPlaying?: boolean
  autoPlay?: boolean
  triggerStart?: number | null
}

export default function VideoPlayer({
  id,
  video,
  videoApps,
  timestamp,
  externalHighlightedApp,
  isExpanded,
  withTitle = true,
  index,
  onClickAction,
  defaultPlaying = false,
  autoPlay = false,
  triggerStart,
  timestampEnd,
}: VideoProps) {
  const { name } = video
  const { embedUrl, sourceKey } = getVideoSource(video)
  const isYoutube = sourceKey?.startsWith('youtube:') ?? false
  const youtubeId = isYoutube ? (sourceKey as string).slice('youtube:'.length) : null

  const [isPlaying, setIsPlaying] = useState(defaultPlaying)
  const [startTime, setStartTime] = useState<number | null>(triggerStart ?? null)

  const timedEmbedUrl = embedUrl
    ? [embedUrl, startTime !== null ? `start=${startTime}` : null].filter(Boolean).join('&')
    : embedUrl

  // Sync startTime to the `timestamp` prop only when the prop changes AND the
  // video is paused. `isPlaying` is intentionally omitted from the deps: if
  // it were included, every play/pause toggle would re-fire with the same
  // `timestamp` and clobber whatever the user has scrubbed to.
  useEffect(() => {
    if (isYoutube) return
    if (timestamp && !isPlaying) {
      setStartTime(timeToSeconds(timestamp))
    }
  }, [isYoutube, timestamp])

  // Handle autoplay when video becomes selected
  useEffect(() => {
    if (isYoutube) return
    if (autoPlay) {
      setIsPlaying(true)
    }
  }, [isYoutube, autoPlay])

  // Retrigger play
  useEffect(() => {
    if (isYoutube) return
    if (triggerStart !== null && triggerStart !== undefined) {
      setStartTime(triggerStart)
    }
  }, [isYoutube, triggerStart])

  // === YouTube: hand playback off to the shared SegmentPlayer ===
  // This lets the same live player seamlessly move between this inline anchor
  // and SegmentPlayer's floating popout as it scrolls in/out of view, instead
  // of owning its own iframe.
  const playerKey = useId()
  const { play, registerAnchor, activePlayerKey } = useSegmentPlayer()
  const isActive = isYoutube && activePlayerKey === playerKey
  const anchorRef = useRef<HTMLDivElement | null>(null)

  // Registration must run before the autoplay/triggerStart effect below so
  // SegmentPlayer already knows this instance's anchor by the time play() is
  // called (relies on effects running in declaration order).
  useEffect(() => {
    if (!isYoutube) return
    registerAnchor(playerKey, anchorRef.current)
    return () => registerAnchor(playerKey, null)
  }, [isYoutube, playerKey, registerAnchor])

  // Thumbnail metadata is handed to SegmentPlayer but isn't a playback input.
  // Hold it in a ref so editing it never recreates playYoutube — the autoplay/
  // triggerStart effect below depends on playYoutube, so a new callback identity
  // would retrigger playback. The ref keeps the values fresh without the dep.
  const thumbnailRef = useRef({
    thumbnail: video.thumbnail,
    thumbnailOverride: video.thumbnailOverride,
  })
  // Sync in an effect rather than during render (no render-time side effects).
  // Declared before the autoplay/triggerStart effect so the ref is already fresh
  // when that effect calls playYoutube.
  useEffect(() => {
    thumbnailRef.current = {
      thumbnail: video.thumbnail,
      thumbnailOverride: video.thumbnailOverride,
    }
  }, [video.thumbnail, video.thumbnailOverride])

  const playYoutube = useCallback(
    (startSeconds: number, endSeconds?: number) => {
      if (!youtubeId) return
      play({
        playerKey,
        youtubeId,
        startSeconds,
        endSeconds,
        segmentName: name,
        segmentId: video.id,
        videoName: name,
        videoId: video.id,
        thumbnail: normalizeThumbnailRef(thumbnailRef.current.thumbnail),
        thumbnailOverride: normalizeThumbnailRef(thumbnailRef.current.thumbnailOverride),
      })
    },
    [play, playerKey, youtubeId, name, video.id],
  )

  // Single consolidated effect for both autoplay and triggerStart (rather than
  // the two separate legacy effects above) to avoid the double-apply race
  // SegmentPlayer's own loading logic warns about when switching videos
  // quickly. `timestamp`/`timestampEnd` are read for their current value, not
  // depended on, so a timestamp change alone (without autoPlay/triggerStart)
  // doesn't force a replay - same semantics as the legacy effects.
  useEffect(() => {
    if (!isYoutube) return
    if (!autoPlay && (triggerStart === null || triggerStart === undefined)) return
    const startSeconds = triggerStart ?? (timestamp ? timeToSeconds(timestamp) : 0)
    const endSeconds = timestampEnd ? timeToSeconds(timestampEnd) : undefined
    playYoutube(startSeconds, endSeconds)
  }, [isYoutube, autoPlay, triggerStart, playYoutube])

  const handleMarkerClick = (ts: string) => {
    if (isYoutube) {
      playYoutube(timeToSeconds(ts))
      return
    }
    setStartTime(timeToSeconds(ts))
    setIsPlaying(true)
  }

  const handleClick = () => {
    if (onClickAction) {
      onClickAction()
      return
    }
    if (isYoutube) {
      const startSeconds = triggerStart ?? (timestamp ? timeToSeconds(timestamp) : 0)
      const endSeconds = timestampEnd ? timeToSeconds(timestampEnd) : undefined
      playYoutube(startSeconds, endSeconds)
      return
    }
    setIsPlaying(!isPlaying)
  }

  const thumbnailForPreconnect = getThumbnail(video)
  const isYoutubeThumbnail = thumbnailForPreconnect?.source === 'youtube'
  return (
    <div id={id ?? 'video-player'} className="flex flex-col gap-5">
      {isYoutubeThumbnail && <YoutubePreconnect />}
      {withTitle && (
        <Tooltip text={name} delay={true} position="down" wrap={true}>
          <p className="w-full truncate font-semibold">{name}</p>
        </Tooltip>
      )}
      <div className="relative aspect-video overflow-hidden rounded-xl">
        {isYoutube ? (
          <>
            {/* Always mounted (never conditionally) so SegmentPlayer has a
              stable anchor to register/portal into - only its visibility
              toggles with isActive. */}
            <div ref={anchorRef} className={cn('h-full w-full', !isActive && 'hidden')} />
            {!isActive && (
              <VideoPlaceholder
                onMarkerClickAction={handleMarkerClick}
                video={video}
                videoApps={videoApps}
                timestamp={timestamp}
                externalHighlightedApp={externalHighlightedApp}
                isExpanded={isExpanded}
                index={index}
                onClickAction={handleClick}
              />
            )}
          </>
        ) : isPlaying && timedEmbedUrl ? (
          <iframe
            width="560"
            height="315"
            title={name ? name : 'Efficient App'}
            src={timedEmbedUrl}
            className="meticulous-ignore aspect-video h-full w-full"
            frameBorder="0"
            allowFullScreen
            allow="autoplay; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            loading="lazy"
            data-review="ignore"
          />
        ) : (
          <VideoPlaceholder
            onMarkerClickAction={handleMarkerClick}
            video={video}
            videoApps={videoApps}
            timestamp={timestamp}
            externalHighlightedApp={externalHighlightedApp}
            isExpanded={isExpanded}
            index={index}
            onClickAction={handleClick}
          />
        )}
      </div>
    </div>
  )
}

export function VideoPlaceholder({
  video,
  videoApps,
  timestamp,
  externalHighlightedApp,
  isExpanded,
  index,
  onClickAction,
  onMarkerClickAction,
}: VideoProps) {
  // `hoveredApp` tracks hovering a marker directly on the player; it takes
  // priority over `externalHighlightedApp`, which lets something outside the
  // player (e.g. hovering an app chip on the video thumbnail list) drive the
  // same highlighted state.
  const [hoveredApp, setHoveredApp] = useState<string | null>(null)
  const highlightedApp = hoveredApp ?? externalHighlightedApp ?? null

  const { name } = video

  const totalDuration = video?.duration
  const durationInitial =
    highlightedApp !== null
      ? videoApps?.find((va) => highlightedApp === (va.app as App).slug)?.timestampStart ?? '0:00'
      : timestamp ?? '0:00'

  // A 0:00–0:00 pair is a comparison-only association - it links the app to
  // the video for discovery but must not render on the timeline at all.
  const visibleVideoApps = (videoApps ?? []).filter(
    (va) => !(va.timestampStart === '0:00' && va.timestampEnd === '0:00'),
  )

  const hasVisibleAppMarkers = !!totalDuration && visibleVideoApps.some((va) => !!va.timestampStart)

  // Group apps that share the exact same start/end so they render as a single
  // marker - one shared bar (or point), with stacked icons - instead of
  // duplicating overlapping markers. (`order` is not used here: it only
  // marks the primary video on a review page, not display priority.)
  const markerGroups = Array.from(
    visibleVideoApps
      .reduce((groups, va) => {
        const key = `${va.timestampStart ?? '0:00'}__${va.timestampEnd ?? ''}`
        const group = groups.get(key) ?? []
        group.push(va)
        groups.set(key, group)
        return groups
      }, new Map<string, VideoAppsApp[]>())
      .values(),
  )

  const thumbnail = video ? getThumbnail(video) : null

  return (
    <button
      onClick={onClickAction}
      className="group absolute left-0 top-0 flex h-full w-full items-center justify-center [container-type:inline-size]"
    >
      <div
        className={cn(
          'absolute bottom-0 z-20 flex w-full items-center justify-between gap-4 px-5 py-4.5 text-white',
          {
            'justify-end p-1': index && index > 0,
          },
        )}
      >
        <Icon name="media-play" className="mr-1" />

        <div className="relative h-1 w-full rounded-full bg-white/20">
          {hasVisibleAppMarkers &&
            markerGroups.map((group, i) => (
              <AppMarkerStack
                key={i}
                appVideos={group}
                totalDuration={totalDuration!}
                onMarkerClickAction={onMarkerClickAction}
                highlightedApp={highlightedApp}
                setHighlightedApp={setHoveredApp}
                isExpanded={isExpanded}
              />
            ))}
        </div>

        <div className="flex items-center gap-2 text-13 font-medium">
          <span className="hidden min-w-[34px] text-right xs:inline">{durationInitial}</span>
          <span className="hidden text-white/20 xs:inline">/</span>
          <span>{totalDuration}</span>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 z-10 h-1/2 w-full bg-gradient-to-t from-black from-15% to-transparent opacity-50 transition-opacity group-hover:opacity-90 md:opacity-75"></div>

      {thumbnail ? (
        <VideoThumbnailImage
          key={thumbnail.url}
          thumbnail={thumbnail}
          className="h-full w-full object-cover"
          loading="lazy"
          decoding="async"
          alt={name ? name : 'Efficient App'}
          sizes={'(min-width: 768px) 50vw, 100vw'}
          fill
        />
      ) : (
        <div className="absolute h-full w-full bg-black"></div>
      )}
    </button>
  )
}

// Shared geometry for a marker's position/width/label on the progress bar.
function computeMarkerGeometry({
  timestampStart,
  timestampEnd,
  totalSeconds,
}: {
  timestampStart?: string | null
  timestampEnd?: string | null
  totalSeconds: number
}) {
  const hasTimestampEnd = !!timestampEnd && timeToSeconds(timestampEnd) > 0

  const start = timeToSeconds(timestampStart || '0:00')
  const leftPercent = (start / totalSeconds) * 100
  const segmentWidthPercent = hasTimestampEnd
    ? ((timeToSeconds(timestampEnd!) - start) / totalSeconds) * 100
    : 0

  // A marker with no end is a "jump to this point" link, so it shows "Play"
  // instead of a duration.
  const pillLabel = hasTimestampEnd
    ? formatDuration(Math.max(0, timeToSeconds(timestampEnd!) - start))
    : 'Play'

  return { leftPercent, hasTimestampEnd, segmentWidthPercent, pillLabel }
}

function AppMarkerStack({
  appVideos,
  totalDuration,
  onMarkerClickAction,
  highlightedApp,
  setHighlightedApp,
  isExpanded,
}: {
  appVideos: VideoAppsApp[]
  totalDuration: string
  onMarkerClickAction?: (timestamp: string) => void
  highlightedApp?: string | null
  setHighlightedApp: (slug: string | null) => void
  isExpanded?: boolean
}) {
  const groupRows = appVideos.filter((av): av is VideoAppsApp & { app: App } =>
    isPopulatedObject<App>(av.app),
  )

  if (groupRows.length === 0) return null

  const total = timeToSeconds(totalDuration)
  if (!Number.isFinite(total) || total <= 0) return null

  // Every row in the group shares the same start/end (that's how the group
  // was formed), so the first row's timestamps describe the whole group.
  const [representative] = groupRows
  const { leftPercent, hasTimestampEnd, segmentWidthPercent, pillLabel } = computeMarkerGeometry({
    timestampStart: representative.timestampStart,
    timestampEnd: representative.timestampEnd,
    totalSeconds: total,
  })

  const apps = groupRows.map((av) => av.app)
  const primaryApp = apps[0]
  const stackSlugs = apps.map((app) => app.slug)
  const highlight = isExpanded || (highlightedApp != null && stackSlugs.includes(highlightedApp))

  return (
    <div
      className={cn('absolute bottom-0', highlight ? 'z-50' : 'z-40')}
      aria-label={`Watch ${apps.map((app) => app.name).join(', ')} Section`}
      style={{
        left: `${leftPercent}%`,
        width: hasTimestampEnd ? `${segmentWidthPercent}%` : '4px',
        height: '88px',
      }}
      onMouseEnter={() => setHighlightedApp(primaryApp.slug)}
      onMouseLeave={() => setHighlightedApp(null)}
      onClick={(e) => {
        e.stopPropagation()
        onMarkerClickAction?.(representative.timestampStart ?? '0:00')
      }}
    >
      {!hasTimestampEnd && (
        <span className="absolute left-0 top-0 h-full w-16 -translate-x-1/2"></span>
      )}
      <div
        className={cn(
          'absolute bottom-0 left-1/2 h-1 origin-center -translate-x-1/2 rounded-full bg-white transition-[width,opacity] duration-300',
          highlight ? 'w-full opacity-100' : 'w-1 opacity-50',
        )}
      />

      <div
        className="absolute bottom-0"
        style={{ left: hasTimestampEnd ? '50%' : '1.5px', transform: 'translateX(-50%)' }}
      >
        <div
          className={cn(
            'absolute left-0 flex -translate-x-1/2 flex-col-reverse items-center gap-1 transition-all duration-150',
            highlight ? 'bottom-12' : 'bottom-3',
            highlightedApp && !stackSlugs.includes(highlightedApp) && 'opacity-50',
          )}
        >
          {apps.map((app, index) => {
            const { backgroundColor } = getBrandColors(app)
            const { appIcon, appGlyph } = checkAppBranding(app)

            return (
              <div
                key={app.slug}
                className={cn(
                  'squircle relative aspect-square min-w-[24px] max-w-10 transition-transform',
                  highlight ? 'w-10' : 'w-[6cqw] sm:w-[5cqw] md:w-[4cqw] lg:w-[3.25cqw]',
                )}
                style={{
                  zIndex: apps.length - index,
                  transform: highlight ? 'translateY(0)' : `translateY(${index * 50}%)`,
                }}
              >
                <AppIcon
                  icon={appIcon}
                  glyph={appGlyph}
                  name={app.name}
                  background={backgroundColor}
                  size={0}
                  className="z-20"
                />

                {index !== apps.length - 1 && (
                  <span
                    className={cn(
                      'absolute left-0 top-0 z-10 h-5 w-full -translate-y-2.5 bg-gradient-to-t from-black/60 to-transparent transition-opacity',
                      highlight ? 'opacity-0' : 'opacity-100',
                    )}
                  ></span>
                )}
              </div>
            )
          })}
        </div>
        <TimestampPill timestamp={pillLabel} visible={highlight} />
      </div>
    </div>
  )
}

export function TimestampPill({
  timestamp,
  visible = false,
}: {
  timestamp: string
  visible?: boolean
}) {
  return (
    <span
      className={cn(
        'absolute left-1/2 flex h-6 -translate-x-1/2 items-center justify-center gap-1 whitespace-nowrap rounded-full bg-white/90 pl-2 pr-2.5 text-13 font-semibold text-black backdrop-blur-md transition-all',
        "before:absolute before:left-1/2 before:top-full before:-translate-x-1/2 before:border-[5px] before:border-transparent before:border-t-white/90 before:backdrop-blur-md before:content-['']",
        visible ? '-top-10 scale-100 opacity-100' : '-top-5 scale-50 opacity-0',
      )}
    >
      <Icon name="media-play" size={12} />
      {timestamp}
    </span>
  )
}
